import { Alert, Input, Modal, message } from '@duxton-web/ui';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import i18n from 'i18n-js';
import _ from 'lodash';
import {
  cpApprove,
  cpCreateEvent,
  cpGetCountries,
  cpListEvents,
  cpRecall,
  cpReject,
  cpUpdateEvent,
} from 'omega-rtc/actions/ucd';
import {
  EventConfigApi,
  EventFormValue,
  EventValidationErrors,
} from 'omega-rtc/types/ts/content-config';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { browserHistory } from 'react-router';

import {
  EVENT_DATE_FORMAT,
  STATUS_PENDING,
  countryFromApi,
  eventFromApi,
  eventToApiBody,
  localeSorter,
  sameEventBody,
} from './adapters';
import { EventDetail, EventList } from './EventsView';
import {
  EMPTY_CONTENT_CONFIG_PERMISSIONS,
  canRecallPendingChange,
  canReviewPendingChange,
  canSubmitEventForApproval,
} from './permissions';
import useContentConfigWrite from './useContentConfigWrite';

// Strict parsing so "2026-13-01" and "01-02-2026" are rejected the same way
// the backend's time.Parse rejects them.
dayjs.extend(customParseFormat);

const DRAFT_ROW_KEY = '__event_draft__';
const ALL_COUNTRIES_KEY = '__all__';
const EMPTY_ARRAY: any[] = [];
const EMPTY_EVENTS_BY_COUNTRY: EventsByCountry = {};
const EMPTY_REQUEST: RequestState = {};

type EventRecord = ReturnType<typeof eventFromApi>;

interface EventPair {
  active?: EventRecord;
  country: string;
  key: string;
  pending?: EventRecord;
  rowKey: string;
}

type EventsByCountry = Record<string, {
  active?: EventConfigApi[];
  pending?: EventConfigApi[];
}>;

interface RequestState {
  isFailure?: boolean;
  isPending?: boolean;
}

const eventText = (key, options = {}) =>
  i18n.t(`ucd_content_config.events.${key}`, options);

export const createEventDraft = (): EventFormValue => ({
  country: '',
  description: '',
  endDate: '',
  isDraft: true,
  name: '',
  startDate: '',
});

const editorFromEvent = (event: EventRecord): EventFormValue => ({
  country: event.country || '',
  description: event.description,
  endDate: event.endDate,
  isDraft: false,
  name: event.name,
  startDate: event.startDate,
});

export const mergeEventConfigs = (
  activeRaw: EventConfigApi[],
  pendingRaw: EventConfigApi[],
): Record<string, EventPair> => {
  const result: Record<string, EventPair> = {};
  const add = (config: EventConfigApi, slot: 'active' | 'pending') => {
    const event = eventFromApi(config);
    const rowKey = `${event.country}:${event.key}`;
    result[rowKey] = {
      ...(result[rowKey] || {}),
      country: event.country,
      key: event.key,
      rowKey,
      [slot]: event,
    };
  };
  activeRaw.forEach((config) => add(config, 'active'));
  pendingRaw.forEach((config) => add(config, 'pending'));
  return result;
};

const isValidEventDate = (value: string) =>
  dayjs(String(value || '').trim(), EVENT_DATE_FORMAT, true).isValid();

// Mirrors the backend's validateEventBody: name and description required after
// trimming, both dates required and strictly formatted, and end not before
// start. Equal dates are a valid single-day event, and past dates are allowed.
export const validateEventForm = (
  candidate: EventFormValue,
): EventValidationErrors => {
  const errors: EventValidationErrors = {};
  if (!String(candidate.name || '').trim()) errors.name = eventText('validation.name');
  if (candidate.isDraft && !candidate.country) {
    errors.country = eventText('validation.country');
  }
  if (!String(candidate.description || '').trim()) {
    errors.description = eventText('validation.description');
  }
  if (!isValidEventDate(candidate.startDate) || !isValidEventDate(candidate.endDate)) {
    errors.dateRange = eventText('validation.date_range');
  } else if (
    dayjs(candidate.endDate, EVENT_DATE_FORMAT, true)
      .isBefore(dayjs(candidate.startDate, EVENT_DATE_FORMAT, true))
  ) {
    errors.dateRange = eventText('validation.date_range_order');
  }
  return errors;
};

const EventsPage = ({
  contentConfigPermissions = EMPTY_CONTENT_CONFIG_PERMISSIONS,
  navTick,
}) => {
  const dispatch = useDispatch();
  const userEmail: string = useSelector((state) =>
    _.get(state, 'app.user.email', ''));
  const cpCountries: any[] = useSelector((state) =>
    _.get(state, 'ucd.cpCountries', EMPTY_ARRAY));
  const eventsByCountry: EventsByCountry = useSelector((state) =>
    _.get(state, 'ucd.cpEventsByCountry', EMPTY_EVENTS_BY_COUNTRY));
  const activeListRequest: RequestState = useSelector((state) =>
    _.get(state, 'requests.cpListEventsRequest', EMPTY_REQUEST));
  const pendingListRequest: RequestState = useSelector((state) =>
    _.get(state, 'requests.cpListEventsPendingRequest', EMPTY_REQUEST));
  const createRequest: RequestState = useSelector((state) =>
    _.get(state, 'requests.cpCreateEventRequest', EMPTY_REQUEST));
  const updateRequest: RequestState = useSelector((state) =>
    _.get(state, 'requests.cpUpdateEventRequest', EMPTY_REQUEST));

  const countries = useMemo(
    () => cpCountries.map(countryFromApi).sort(localeSorter('name')),
    [cpCountries],
  );
  useEffect(() => {
    dispatch(cpGetCountries());
  }, [dispatch]);

  const [countryFilter, setCountryFilter] = useState('');
  // Captured once on mount so a shared URL opens the right event; cleared as
  // soon as it resolves so it cannot re-select after the user navigates away.
  const [deepLink, setDeepLink] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      country: params.get('eventCountry') || '',
      key: params.get('eventKey') || '',
    };
  });
  const [listTab, setListTab] = useState('all');
  const [selectedRowKey, setSelectedRowKey] = useState('');
  const [form, setForm] = useState<EventFormValue | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showPending, setShowPending] = useState(false);
  const [validationErrors, setValidationErrors] =
    useState<EventValidationErrors>({});

  const countryKey = countryFilter || ALL_COUNTRIES_KEY;
  const activeRaw = _.get(eventsByCountry, [countryKey, 'active'], EMPTY_ARRAY);
  const pendingRaw = _.get(eventsByCountry, [countryKey, 'pending'], EMPTY_ARRAY);
  const merged = useMemo(
    () => mergeEventConfigs(activeRaw, pendingRaw),
    [activeRaw, pendingRaw],
  );
  const listPending = Boolean(activeListRequest.isPending)
    || Boolean(pendingListRequest.isPending);

  const refetchEvents = useCallback(() => {
    dispatch(cpListEvents(countryFilter));
    dispatch(cpListEvents(countryFilter, STATUS_PENDING));
  }, [countryFilter, dispatch]);
  useEffect(() => {
    refetchEvents();
  }, [refetchEvents]);

  const resetToList = useCallback(() => {
    setSelectedRowKey('');
    setForm(null);
    setIsEditing(false);
    setShowPending(false);
    setValidationErrors({});
  }, []);

  useEffect(() => {
    resetToList();
  }, [navTick, resetToList]);

  const clearDeepLink = useCallback(
    () => setDeepLink({ country: '', key: '' }),
    [],
  );

  // The list defaults to every country, so a deep-linked event is in the
  // first fetch regardless of its country.
  const sawListLoad = useRef(false);
  useEffect(() => {
    if (!deepLink.key) return;
    const rowKey = `${deepLink.country}:${deepLink.key}`;
    if (merged[rowKey]) {
      const pair = merged[rowKey];
      const editable = Boolean(
        pair.active && !pair.pending && contentConfigPermissions.canUpdate,
      );
      setSelectedRowKey(rowKey);
      setForm(editable ? editorFromEvent(pair.active) : null);
      setIsEditing(editable);
      clearDeepLink();
      return;
    }
    // Give the in-flight list a chance to arrive before giving up on the link.
    if (listPending) sawListLoad.current = true;
    else if (sawListLoad.current) clearDeepLink();
  }, [
    clearDeepLink,
    contentConfigPermissions.canUpdate,
    deepLink.country,
    deepLink.key,
    listPending,
    merged,
  ]);

  const [locationTick, setLocationTick] = useState(0);
  useEffect(() => {
    const unlisten = browserHistory.listen(
      () => setLocationTick((tick) => tick + 1),
    );
    return unlisten;
  }, []);

  // Mirror the open event into the URL so the detail is shareable. Only the
  // two owned params are touched; anything else in the query is preserved.
  useEffect(() => {
    if (deepLink.key) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab') !== 'content-config') return;
    const before = params.toString();
    const pair = selectedRowKey && selectedRowKey !== DRAFT_ROW_KEY
      ? merged[selectedRowKey]
      : null;
    const event = pair && (pair.active || pair.pending);
    if (event) {
      params.set('eventCountry', event.country);
      params.set('eventKey', event.key);
    } else {
      params.delete('eventCountry');
      params.delete('eventKey');
    }
    const after = params.toString();
    if (after !== before) {
      browserHistory.replace(`${window.location.pathname}?${after}`);
    }
  }, [deepLink.key, locationTick, merged, selectedRowKey]);

  // Leaving the tab drops the params it owns, so they cannot select an event
  // on a page that no longer shows one.
  useEffect(() => () => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('eventKey') && !params.has('eventCountry')) return;
    params.delete('eventCountry');
    params.delete('eventKey');
    const query = params.toString();
    browserHistory.replace(
      `${window.location.pathname}${query ? `?${query}` : ''}`,
    );
  }, []);

  const countryLabel = useCallback((country) => (
    (countries.find((item) => item.id === country) || {}).name || country || '—'
  ), [countries]);

  const rows = useMemo(() => Object.values(merged).map((pair) => {
    const current = pair.active || pair.pending;
    return {
      countryLabel: countryLabel(current.country),
      endDate: current.endDate,
      name: current.name,
      rowKey: pair.rowKey,
      startDate: current.startDate,
      status: pair.pending ? 'pending' : 'active',
      updatedAt: current.updatedAt,
      updatedBy: current.updatedBy || current.createdBy,
    };
  }), [countryLabel, merged]);

  const visibleRows = useMemo(
    () => rows.filter((row) => listTab !== 'pending' || row.status === 'pending'),
    [listTab, rows],
  );

  const selectedPair = merged[selectedRowKey];
  const active = selectedPair && selectedPair.active;
  const pending = selectedPair && selectedPair.pending;
  const isDraft = selectedRowKey === DRAFT_ROW_KEY;
  const record = isDraft
    ? form
    : selectedPair && (showPending && pending ? pending : active || pending);

  // Drop the detail when the selected row disappears from a refreshed list
  // (e.g. it was approved into a different country filter).
  useEffect(() => {
    if (selectedRowKey && selectedRowKey !== DRAFT_ROW_KEY
      && !merged[selectedRowKey] && !listPending) {
      resetToList();
    }
  }, [listPending, merged, resetToList, selectedRowKey]);

  const listError = Boolean(activeListRequest.isFailure)
    || Boolean(pendingListRequest.isFailure);
  const writeError = isDraft
    ? Boolean(createRequest.isFailure)
    : Boolean(updateRequest.isFailure);

  const onWriteSuccess = useCallback((options: { country?: string } = {}) => {
    resetToList();
    // A create can target a country the list is not scoped to. Follow it, so
    // the new event is visible instead of landing outside the current filter.
    if (options.country !== undefined && options.country !== countryFilter) {
      setCountryFilter(options.country);
      // Changing the filter re-runs the fetch effect; refetching here too would
      // just request the country we are leaving.
      return;
    }
    refetchEvents();
  }, [countryFilter, refetchEvents, resetToList]);
  const onWriteFailure = useCallback(() => {
    refetchEvents();
  }, [refetchEvents]);
  const { busy, runWrite } = useContentConfigWrite({
    onFailure: onWriteFailure,
    onSuccess: onWriteSuccess,
  });

  const notifyForbidden = () => message.error(eventText('permissions.forbidden'));

  const onCreate = () => {
    if (!contentConfigPermissions.canCreate) {
      notifyForbidden();
      return;
    }
    clearDeepLink();
    setSelectedRowKey(DRAFT_ROW_KEY);
    setForm({
      ...createEventDraft(),
      // Pre-fill from the list filter so the common case needs no extra pick.
      country: countryFilter,
    });
    setIsEditing(true);
    setShowPending(false);
    setValidationErrors({});
  };

  const onOpenDetail = (rowKey) => {
    // An explicit selection supersedes any link that never resolved; leaving it
    // set would keep the URL-mirroring effect disabled.
    clearDeepLink();
    const pair = merged[rowKey];
    const editable = Boolean(
      pair
      && pair.active
      && !pair.pending
      && contentConfigPermissions.canUpdate,
    );
    setSelectedRowKey(rowKey);
    setForm(editable ? editorFromEvent(pair.active) : null);
    setIsEditing(editable);
    setShowPending(false);
    setValidationErrors({});
  };

  const onChange = (
    field: 'country' | 'description' | 'name',
    value: string | undefined,
  ) => {
    setValidationErrors((current) => ({ ...current, [field]: undefined }));
    setForm((current) => current && ({ ...current, [field]: value }));
  };

  const onDateRangeChange = (startDate: string, endDate: string) => {
    setValidationErrors((current) => ({ ...current, dateRange: undefined }));
    setForm((current) => current && ({ ...current, startDate, endDate }));
  };

  const hasChanges = isDraft || Boolean(
    active && !pending && form && !sameEventBody(active, form),
  );

  const onSubmit = () => {
    if (!form) return;
    const errors = validateEventForm(form);
    setValidationErrors(errors);
    if (Object.keys(errors).length) return;

    const hasEdits = Boolean(active) && !sameEventBody(form, active);
    const canSubmit = canSubmitEventForApproval(contentConfigPermissions, {
      isDraft,
      isValid: true,
      hasActive: Boolean(active),
      hasEdits,
      hasPending: Boolean(pending),
    });
    if (!canSubmit) {
      if (!isDraft && !hasEdits) message.error(eventText('validation.no_changes'));
      else notifyForbidden();
      return;
    }

    const body = eventToApiBody(form);
    if (isDraft) {
      runWrite(
        cpCreateEvent(form.country, body, userEmail),
        'createEvent',
        { country: form.country },
      );
      return;
    }
    runWrite(cpUpdateEvent(active.id, body, userEmail), 'updateEvent');
  };

  const isMine = Boolean(
    pending && (pending.updatedBy || pending.createdBy) === userEmail,
  );
  const canRecall = Boolean(pending)
    && canRecallPendingChange(contentConfigPermissions, isMine);
  const canReview = Boolean(pending)
    && canReviewPendingChange(contentConfigPermissions, isMine);

  const onApprove = () => {
    if (!pending || !canReview) {
      notifyForbidden();
      return;
    }
    runWrite(cpApprove(pending.id, userEmail), 'approve');
  };
  const onReject = (comment) => {
    if (!pending || !canReview || !comment.trim()) {
      notifyForbidden();
      return;
    }
    runWrite(cpReject(pending.id, userEmail, comment.trim()), 'reject');
  };
  const onRequestReject = () => {
    if (!pending || !canReview) {
      notifyForbidden();
      return;
    }
    let comment = '';
    Modal.confirm({
      title: eventText('messages.reject_title'),
      content: (
        <Input.TextArea
          placeholder={eventText('placeholders.rejection_comment')}
          onChange={(event) => { comment = event.target.value; }}
        />
      ),
      okText: eventText('actions.reject'),
      okType: 'danger',
      onOk: () => {
        if (!comment.trim()) {
          message.error(eventText('validation.rejection_comment'));
          return Promise.reject();
        }
        onReject(comment);
        return Promise.resolve();
      },
    });
  };
  const onRecall = () => {
    if (!pending || !canRecall) {
      notifyForbidden();
      return;
    }
    runWrite(cpRecall(pending.id, userEmail), 'recall');
  };

  return (
    <div className="cc-events-page">
      {selectedRowKey ? (
        <React.Fragment>
          {writeError && isEditing && (
            <Alert
              type="error"
              showIcon
              role="alert"
              className="cc-action-bar"
              message={eventText('errors.write')}
            />
          )}
          <EventDetail
            active={active}
            busy={busy}
            canRecall={canRecall}
            canReview={canReview}
            countries={countries}
            errors={validationErrors}
            form={form}
            hasChanges={hasChanges}
            isDraft={isDraft}
            isEditing={isEditing}
            onApprove={onApprove}
            onCancelEdit={() => {
              if (isDraft) resetToList();
              else {
                setForm(editorFromEvent(active));
                setIsEditing(true);
                setValidationErrors({});
              }
            }}
            onChange={onChange}
            onDateRangeChange={onDateRangeChange}
            onRecall={onRecall}
            onReject={onReject}
            onRequestReject={onRequestReject}
            onSubmit={onSubmit}
            pending={pending}
            record={record}
            setShowPending={setShowPending}
            showPending={showPending}
          />
        </React.Fragment>
      ) : (
        <EventList
          canCreate={contentConfigPermissions.canCreate}
          countries={countries}
          countryFilter={countryFilter}
          listError={listError}
          listTab={listTab}
          loading={listPending && rows.length === 0}
          onCreate={onCreate}
          onOpenDetail={onOpenDetail}
          onRetry={refetchEvents}
          rows={visibleRows}
          setCountryFilter={setCountryFilter}
          setListTab={setListTab}
        />
      )}
    </div>
  );
};

export default EventsPage;
